import type { AccessPolicy, ResourceType } from "@medplum/fhirtypes";
import type { Permissions, ResourceAccess, Role } from "../types";

const ROLE_MAP: Record<string, Role> = {
  Admin: "admin",
  Clinician: "clinician",
  Staff: "staff",
  Patient: "patient",
  Assistant: "assistant",
};

const NO_ACCESS: ResourceAccess = { read: false, write: false };
const FULL_ACCESS: ResourceAccess = { read: true, write: true };

export function parseAccessPolicy(policy: AccessPolicy | null | undefined, isProjectAdmin: boolean): Permissions {
  const role = policy?.name ? (ROLE_MAP[policy.name] ?? null) : null;
  const isAdmin = isProjectAdmin || role === "admin";

  const hasAccess = (resourceType: ResourceType): ResourceAccess => {
    // Admins have full access
    if (isAdmin) {
      return FULL_ACCESS;
    }

    // No policy means no access
    if (!policy?.resource) {
      return NO_ACCESS;
    }

    // Check for exact resource type match
    const exactMatch = policy.resource.find((r) => r.resourceType === resourceType);
    if (exactMatch) {
      return { read: true, write: !exactMatch.readonly };
    }

    // Check for wildcard fallback
    const wildcard = policy.resource.find((r) => r.resourceType === "*");
    if (wildcard) {
      return { read: true, write: !wildcard.readonly };
    }

    return NO_ACCESS;
  };

  return {
    role,
    isAdmin,
    accessPolicy: policy ?? null,
    canRead: (rt: ResourceType) => hasAccess(rt).read,
    canWrite: (rt: ResourceType) => hasAccess(rt).write,
    hasAccess,
  };
}
